import { useEffect, useRef, useState } from "react";
import MobileFrame from "../../../components/MobileFrame";
import {
    ChevronLeft,
    Landmark,
    ShieldCheck,
    Loader2,
    CheckCircle2,
    ExternalLink,
    Building2,
    Hash,
    UserRound,
    Wallet,
    QrCode,
    AtSign,
    RotateCcw,
    Smartphone,
    ListChecks,
    KeyRound,
    Info,
    ChevronRight,
    Zap,
    Sparkles
} from "lucide-react";
import Lottie from "lottie-react";
import loaderAnimation from "../../../assets/POS Accept Payment Loader.json";
import { journeyAction } from "../../../api/journey.api";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../store";
import { setJourney } from "../../../store/journeySlice";


type Step =
    | "method"
    | "upiEntry"
    | "upiVerifying"
    | "upiAutopaySetup"
    | "upiRequesting"
    | "upiPaymentWaiting"
    | "upiCompleted"
    | "manualForm"
    | "manualRedirected"
    | "manualCompleted";

type AccountType = "Saving" | "Current";

interface BankingDetails {
    // bankName: string;
    // branchName: string;
    ifsc: string;
    accountNumber: string;
    // accountHolderName: string;
    // accountType: AccountType;
}

interface SetupAutopayProps {
    availableSubSteps: string[];
    onBack: () => void;
    onDone: () => void;
}

const EMPTY_DETAILS: BankingDetails = {
    // bankName: "",
    // branchName: "",
    ifsc: "",
    accountNumber: "",
    // accountHolderName: "",
    // accountType: "Saving",
};
const MANDATE_REDIRECT_URL =
    "https://stg-native.cleverpe.com/ndashboard/mandate/callback";

const UPI_VERIFY_MS = 7000;
const UPI_REQUEST_MS = 5000;
const PAYMENT_EXPIRY_SECONDS = 300; // 5 minutes — shown as the countdown/expiry
const PAYMENT_AUTOCOMPLETE_MS = 15000; // 15 seconds — demo-only: simulates payment success

/** Small reusable "Powered by CleverPe" footer text with the gradient wordmark. */
const PoweredByFooter = ({ className = "" }: { className?: string }) => (
    <p className={`text-center text-[11px] font-semibold tracking-wide text-gray-400 ${className}`}>
        CleverPe
    </p>
);



/** Purely decorative placeholder QR pattern — not a real scannable code. */
const DummyQr = () => {
    const cells = Array.from({ length: 49 });
    return (
        <div className="grid grid-cols-7 gap-[3px] h-full w-full p-2">
            {cells.map((_, i) => {
                const filled = (i * 7 + 13) % 5 === 0 || [0, 6, 42, 48, 8, 40, 3, 24, 45].includes(i);
                return (
                    <div
                        key={i}
                        className={`rounded-[1px] ${filled ? "bg-gray-800" : "bg-transparent"}`}
                    />
                );
            })}
        </div>
    );
};

const STEP_TITLES: Record<Step, string> = {
    method: "Setup AutoPay",
    upiEntry: "Setup AutoPay via UPI",
    upiVerifying: "Setup AutoPay via UPI",
    upiAutopaySetup: "Enable UPI AutoPay",
    upiRequesting: "Enable UPI AutoPay",
    upiPaymentWaiting: "Complete Payment",
    upiCompleted: "Setup AutoPay",
    manualForm: "Add Banking Details",
    manualRedirected: "Add Banking Details",
    manualCompleted: "Add Banking Details",
};

const SetupAutopay = ({ onBack, onDone, availableSubSteps, }: SetupAutopayProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );
    const hasUpiMandate =
        availableSubSteps.includes("UPI_MANDATE");

    const hasEMandate =
        availableSubSteps.includes("MANDATE") ||
        availableSubSteps.includes("BANKING_DETAILS");
    /*
     * If banking details were already saved in a previous
     * session, don't ask for IFSC/account number again —
     * just let the customer continue to mandate authorization.
     */
    const bankingAlreadySaved =
        Boolean(journey?.resumeMetadata?.MANDATE?.bankingDetailsAdded);

    const [step, setStep] = useState<Step>(
        hasEMandate && !hasUpiMandate
            ? "manualForm"
            : "method"
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ---------- UPI flow state ----------
    const [vpa, setVpa] = useState("");
    const [paymentSecondsLeft, setPaymentSecondsLeft] = useState(PAYMENT_EXPIRY_SECONDS);
    const isVpaValid = /^[\w.\-]{2,}@[a-zA-Z]{2,}$/.test(vpa.trim());
    const [vpaVerifyStatus, setVpaVerifyStatus] = useState<"idle" | "verifying" | "verified">("idle");
    const [accountName, setAccountName] = useState("");

    // ---------- Manual flow state ----------
    const [details, setDetails] = useState<BankingDetails>(EMPTY_DETAILS);
    const [mandateWindow, setMandateWindow] = useState<Window | null>(null);
    const isFormComplete =
        bankingAlreadySaved ||
        Object.values(details).every((v) => v.trim() !== "");

    const isBusy =
        step === "upiVerifying" ||
        step === "upiRequesting" ||
        step === "upiCompleted" ||
        step === "manualRedirected" ||
        step === "manualCompleted";

    const handleChange = (field: keyof BankingDetails, value: string) => {
        setDetails((prev) => ({ ...prev, [field]: value }));
    };

    // ---------- UPI: verify VPA ----------
    const handleVerifyVpa = () => {
        if (vpaVerifyStatus !== "verified") return;
        setPaymentSecondsLeft(PAYMENT_EXPIRY_SECONDS);
        setStep("upiPaymentWaiting");
    };

    // ---------- UPI: request autopay activation payment ----------
    // const handleGetUpiRequest = () => {
    //     setStep("upiRequesting");
    //     // Simulated payment-request generation — swap for the real API call.
    //     setTimeout(() => {
    //         setPaymentSecondsLeft(PAYMENT_EXPIRY_SECONDS);
    //         setStep("upiPaymentWaiting");
    //     }, UPI_REQUEST_MS);
    // };

    // Countdown for the payment-waiting screen.

    // ---------- UPI: inline VPA verification (shown inside the input) ----------
    const handleInlineVerifyVpa = () => {
        if (!isVpaValid || vpaVerifyStatus === "verifying") return;
        setVpaVerifyStatus("verifying");
        // Simulated VPA -> account-name resolution. Swap for the real API call.
        setTimeout(() => {
            setAccountName("Shravani Rahul Sathe");
            setVpaVerifyStatus("verified");
        }, 1800);
    };

    const handleVpaChange = (value: string) => {
        setVpa(value);
        if (vpaVerifyStatus !== "idle") {
            setVpaVerifyStatus("idle");
            setAccountName("");
        }
    };
    useEffect(() => {
        if (step !== "upiPaymentWaiting") return;
        if (paymentSecondsLeft <= 0) return;
        const interval = setInterval(() => {
            setPaymentSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [step, paymentSecondsLeft]);

    // waits the full 2 minutes before completing
    useEffect(() => {
        if (step !== "upiPaymentWaiting") return;
        const timeout = setTimeout(() => {
            setStep("upiCompleted");
            setTimeout(() => onDone(), 1300);
        }, PAYMENT_AUTOCOMPLETE_MS);
        return () => clearTimeout(timeout);
    }, [step, onDone]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    const firstInputRef =
        useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (step !== "manualForm") return;
        if (bankingAlreadySaved) return;

        const frame = requestAnimationFrame(() => {
            firstInputRef.current?.focus();
        });

        return () => cancelAnimationFrame(frame);
    }, [step, bankingAlreadySaved]);

    // ---------- Manual flow: bank mandate popup ----------
    const handleContinueManual = async () => {

        if (!isFormComplete || isSubmitting) {
            return;
        }

        try {

            setIsSubmitting(true);
            setError(null);

            /*
             * Skip re-saving banking details if they were
             * already submitted in a previous session.
             */
            if (!bankingAlreadySaved) {

                const bankingResponse =
                    await journeyAction({
                        subStep: "BANKING_DETAILS",
                        action: "CREATE",
                        payload: {
                            bankingDetails: {
                                ifsc: details.ifsc,
                                accountNumber:
                                    details.accountNumber,
                            },
                        },
                    });

                dispatch(
                    setJourney(
                        bankingResponse.data.journey
                    )
                );
            }


            /*
             * Generate mandate link.
             */
            const mandateResponse =
                await journeyAction({
                    subStep: "E_MANDATE",
                    action: "GENERATE",
                    payload: {
                        redirectUrl:
                            MANDATE_REDIRECT_URL,
                    },
                });


            dispatch(
                setJourney(
                    mandateResponse.data.journey
                )
            );


            const mandateLink =
                mandateResponse.data.metadata?.mandateLink;


            if (!mandateLink) {

                throw new Error(
                    "Mandate link was not returned."
                );

            }


            window.location.href =
                mandateLink;


        } catch (error) {

            console.error(
                "Mandate setup failed:",
                error
            );


            const message =
                error instanceof Error
                    ? error.message
                    : "";


            /*
             * Convert backend/internal errors
             * into customer-friendly messages.
             */
            if (
                message
                    .toLowerCase()
                    .includes(
                        "failed to add banking details for given loan id"
                    )
            ) {

                setError(
                    "We couldn't verify this bank account. Check your details and try again."
                );

            } else if (
                message
                    .toLowerCase()
                    .includes(
                        "Failed to fetch Mandate link"
                    )
            ) {

                setError(
                    "Unable to start mandate setup right now. Please try again."
                );

            } else if (
                message
                    .toLowerCase()
                    .includes(
                        "cannot recreate mandate, limit exhausted"
                    )
            ) {

                setError(
                    "You have reached the maximum number of mandate attempts. Please contact support for assistance."
                )
            } else {

                setError(
                    "Unable to verify your bank details. Please check your information and try again."
                );

            }


            setIsSubmitting(false);
        }
    };

    const handleReopen = () => {
        if (mandateWindow && !mandateWindow.closed) {
            mandateWindow.focus();
            return;
        }
        handleContinueManual();
    };

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === "MANDATE_SUCCESS") {
                setStep("manualCompleted");
                setTimeout(() => onDone(), 1200);
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [onDone]);

    // For this demo, simulate the payment completing on its own after a couple of minutes
    // on the waiting screen — swap for a real payment-status poll / webhook listener.
    useEffect(() => {
        if (step !== "upiPaymentWaiting") return;
        const timeout = setTimeout(() => {
            setStep("upiCompleted");
            setTimeout(() => onDone(), 1300);
        }, PAYMENT_AUTOCOMPLETE_MS);
        return () => clearTimeout(timeout);
    }, [step, onDone]);

    const handleBack = () => {
        if (step === "method") return onBack();
        if (step === "upiEntry") {
            setVpa("");
            setVpaVerifyStatus("idle");
            setAccountName("");
            return setStep("method");
        }
        if (step === "upiAutopaySetup") return setStep("upiEntry");

        if (step === "manualForm") {
            /*
             * If UPI isn't offered, "method" was never actually
             * shown — going back to it would be a single-option
             * dead end. Go straight to the dashboard instead.
             */
            if (!hasUpiMandate) return onBack();
            return setStep("method");
        }
    };

    const showHeader =
        step === "method" ||
        step === "upiEntry" ||
        step === "upiAutopaySetup" ||
        step === "manualForm";

    // The three loading/waiting screens — no logo, footer pinned to the bottom.
    // Loading/waiting screens — no logo, footer pinned to the bottom.
    // Loading/waiting/success screens — no logo, footer pinned to the bottom.
    const isLoadingScreen =
        step === "upiVerifying" ||
        step === "upiRequesting" ||
        step === "upiPaymentWaiting" ||
        step === "upiCompleted" ||
        step === "manualRedirected" ||
        step === "manualCompleted";

    return (
        <MobileFrame glowColor="#c7d2fe" borderColor="#e5e7eb" width={420}>
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background Blobs */}
                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />
                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />

                {/* Logo — top-right on active/interactive steps only, hidden on loader & success screens */}
                {/* Logo — hidden on the 3 loading/waiting screens */}
                {!isLoadingScreen && (
                    <img
                        src="/CleverPeLogos/CleverPE_Black_icon.svg"
                        alt="CleverPe"
                        className="absolute top-6 right-6 z-20 h-6 w-6 opacity-90"
                    />
                )}

                <div className="relative z-10 flex flex-col h-full">

                    {/* Header */}
                    {showHeader && (
                        <div className="flex items-center gap-3 px-6 pt-6 shrink-0">
                            <button
                                onClick={handleBack}
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-gray-600 shadow-sm active:scale-95 transition-transform disabled:opacity-40"
                            >
                                <ChevronLeft className="h-4.5 w-4.5" />
                            </button>
                            <p className="text-base font-bold text-gray-900">
                                {STEP_TITLES[step]}
                            </p>
                        </div>
                    )}

                    {/* ================= STEP: Method Choice ================= */}
                    {step === "method" && (
                        <div className="flex flex-1 flex-col px-6 pt-7 pb-4">
                            <p className="text-[13px] text-gray-500 leading-snug">
                                Choose how you'd like to set up auto-debit for your EMIs
                            </p>

                            <div className="mt-8 space-y-6">

                                {/* ---- UPI Option (recommended) ---- */}
                                {hasUpiMandate && (
                                    <button
                                        onClick={() => {
                                            setVpa("");
                                            setVpaVerifyStatus("idle");
                                            setAccountName("");
                                            setStep("upiEntry");
                                        }}
                                        className="
                    group
                    relative
                    w-full
                    text-left
                    rounded-2xl
                    border
                    border-indigo-300
                    bg-white
                    p-5

                    ring-1
                    ring-indigo-100/70

                    shadow-[0_4px_12px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.05)]

                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:border-indigo-400
                    hover:ring-indigo-200
                    hover:shadow-[0_8px_20px_rgba(15,23,42,0.08),0_18px_40px_rgba(15,23,42,0.10)]
                    active:scale-[0.98]
                "
                                    >
                                        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />

                                        <span className="absolute -top-2.5 right-4 flex items-center gap-1 whitespace-nowrap rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold text-indigo-600 shadow-sm">
                                            <Sparkles className="h-3 w-3" />
                                            Recommended
                                        </span>

                                        <div className="relative z-10 flex items-center gap-3.5">
                                            {/* <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
                                            <Smartphone className="h-6 w-6" strokeWidth={2} />
                                        </div> */}
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-[15px] font-bold text-gray-900 leading-tight">
                                                    UPI AutoPay
                                                </h3>
                                                <p className="mt-0.5 text-[12px] text-gray-500 leading-tight">
                                                    Verify instantly using a QR code or UPI ID
                                                </p>
                                            </div>
                                            <ChevronRight className="h-4.5 w-4.5 shrink-0 text-indigo-400" />
                                        </div>

                                        <div className="relative z-10 mt-4 flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-medium text-emerald-600">
                                                <Zap className="h-4 w-4" />
                                                Instant
                                            </span>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-[12px] font-medium text-slate-600">
                                                <AtSign className="h-4 w-4" />
                                                Verify via UPI ID
                                            </span>
                                        </div>
                                    </button>
                                )}

                                {/* ---- Add Manually Option ---- */}
                                {hasEMandate && (
                                    <button
                                        onClick={() => setStep("manualForm")}
                                        className="
                    group
                    relative
                    w-full
                    text-left
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    p-5

                    shadow-[0_4px_12px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.05)]

                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:border-slate-300
                    hover:shadow-[0_8px_20px_rgba(15,23,42,0.08),0_18px_40px_rgba(15,23,42,0.10)]
                    active:scale-[0.98]
                "
                                    >
                                        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />

                                        <div className="relative z-10 flex items-center gap-3.5">
                                            {/* <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-500">
                                            <Landmark className="h-6 w-6" strokeWidth={2} />
                                        </div> */}
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-[15px] font-bold text-gray-900 leading-tight">
                                                    E-Mandate
                                                </h3>
                                                <p className="mt-0.5 text-[12px] text-gray-500 leading-tight">
                                                    Add your bank details manually
                                                </p>
                                            </div>
                                            <ChevronRight className="h-4.5 w-4.5 shrink-0 text-slate-300" />
                                        </div>

                                        <div className="relative z-10 mt-4 flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-medium text-emerald-600">
                                                <ShieldCheck className="h-4 w-4" />
                                                Secure
                                            </span>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-[12px] font-medium text-slate-600">
                                                <Wallet className="h-4 w-4" />
                                                Keep A/C no. & IFSC ready
                                            </span>
                                        </div>
                                    </button>
                                )}

                            </div>

                            <div className="flex-1" />

                            <PoweredByFooter className="mb-1" />
                        </div>
                    )}

                    {/* ================= STEP: UPI Entry (QR / VPA) ================= */}
                    {(step === "upiEntry" || step === "upiVerifying") && (
                        <div className="flex flex-1 flex-col px-6 pt-7 pb-4">
                            <p className="text-[13px] text-gray-500 leading-snug">
                                Verify via QR or your UPI ID
                            </p>

                            {/* QR option */}
                            <div className="mt-5 p-4 bg-white border border-gray-100 shadow-sm rounded-2xl">
                                <div className="flex items-center gap-2">
                                    <QrCode className="h-4 w-4 text-indigo-500" />
                                    <p className="text-[13px] font-semibold text-gray-900">Scan QR Code</p>
                                </div>
                                <p className="mt-1 text-[11px] text-gray-500 leading-snug">
                                    Scan the QR using any UPI app on your mobile phone — PhonePe, Google Pay, Paytm, etc.
                                </p>
                                <div className="mt-3 flex justify-center">
                                    <div className="h-32 w-32 rounded-xl border border-gray-200 bg-white p-1">
                                        <DummyQr />
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="my-4 flex items-center gap-3">
                                <div className="h-px flex-1 bg-gray-200" />
                                <span className="text-[11px] font-medium text-gray-400">OR</span>
                                <div className="h-px flex-1 bg-gray-200" />
                            </div>

                            {/* VPA option */}
                            <div className="p-4 bg-white border border-gray-100 shadow-sm rounded-2xl">
                                <div className="flex items-center gap-2">
                                    <AtSign className="h-4 w-4 text-indigo-500" />
                                    <p className="text-[13px] font-semibold text-gray-900">Enter UPI ID / VPA</p>
                                </div>

                                <div
                                    className={`mt-3 flex items-center gap-1.5 rounded-xl border px-3 py-2.5 transition-colors ${vpaVerifyStatus === "verified"
                                        ? "border-emerald-300 bg-emerald-50/50"
                                        : "border-slate-200 bg-slate-50"
                                        }`}
                                >
                                    <input
                                        type="text"
                                        value={vpa}
                                        onChange={(e) => handleVpaChange(e.target.value)}
                                        placeholder="yourname@upi"
                                        disabled={vpaVerifyStatus === "verifying"}
                                        className="w-full min-w-0 bg-transparent text-[14px] font-medium text-gray-700 outline-none placeholder:text-gray-400 placeholder:font-normal disabled:opacity-60"
                                    />

                                    {isVpaValid && vpaVerifyStatus === "idle" && (
                                        <button
                                            onClick={handleInlineVerifyVpa}
                                            className="shrink-0 rounded-lg bg-indigo-500 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-indigo-600 active:scale-95"
                                        >
                                            Verify
                                        </button>
                                    )}

                                    {vpaVerifyStatus === "verifying" && (
                                        <div className="shrink-0 flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1.5">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
                                            <span className="text-[11px] font-medium text-indigo-500">Verifying</span>
                                        </div>
                                    )}

                                    {vpaVerifyStatus === "verified" && (
                                        <div className="shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                        </div>
                                    )}
                                </div>

                                {vpaVerifyStatus === "verified" ? (
                                    <div className=" flex items-center rounded-lg px-2.5 py-1.5">
                                        <p className="text-[11px] font-medium text-emerald-500 truncate">
                                            Account Holder: <span className="font-semibold">{accountName}</span>
                                        </p>
                                    </div>
                                ) : (
                                    <p className="mt-2 text-[11px] text-gray-500 leading-snug">
                                        A collect request will be sent to this UPI ID.
                                    </p>
                                )}
                            </div>

                            <div className="flex-1" />

                            <button
                                onClick={handleVerifyVpa}
                                disabled={vpaVerifyStatus !== "verified"}
                                className="w-full py-3 mb-4 text-m font-semibold text-white rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 disabled:opacity-50"
                            >
                                Continue
                            </button>

                            <PoweredByFooter />
                        </div>
                    )}

                    {/* ================= STEP: UPI Verifying (loader) ================= */}
                    {step === "upiVerifying" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8 text-center bg-white">
                            <div className="h-36 w-36">
                                <Lottie animationData={loaderAnimation} loop autoplay />
                            </div>
                            <p className="mt-1 text-[13.5px] font-semibold text-gray-800">
                                Verifying your bank account
                            </p>
                            <p className="mt-1 text-[11.5px] text-gray-500 leading-snug max-w-[260px]">
                                Fetching your information securely from your UPI provider
                            </p>
                            <p className="mt-3 text-[12px] font-semibold text-indigo-600">
                                UPI ID: {vpa}
                            </p>
                            <PoweredByFooter className="absolute bottom-6" />
                        </div>
                    )}

                    {/* ================= STEP: Enable UPI AutoPay ================= */}
                    {/* {(step === "upiAutopaySetup" ||
                        step === "upiRequesting" ||
                        step === "upiPaymentWaiting" ||
                        step === "upiCompleted") && (
                            <div className="flex flex-1 flex-col px-6 pt-5 pb-4 overflow-y-auto">

                                
                                <div className="p-4 bg-white border border-gray-100 shadow-sm rounded-2xl">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-center gap-3">
                                            
                                            <div className="min-w-0">
                                                <p className="text-[14px] text-gray-700 leading-tight">Verification Amount</p>
                                                <p className="text-[13px] font-bold text-gray-900 leading-tight">₹2</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 border-l border-gray-100 pl-3">
                                           
                                            <div className="min-w-0">
                                                <p className="text-[14px] text-gray-700 leading-tight">UPI ID</p>
                                                <p className="text-[13px] font-semibold text-gray-800 leading-tight truncate">{vpa}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                               
                                <div className="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-3.5">
                                    <div className="flex items-center gap-2">
                                        <Info className="h-4.5 w-4.5 shrink-0 text-indigo-500" />
                                        <p className="text-[12.5px] font-semibold text-indigo-800">Why ₹2 Verification?</p>
                                    </div>
                                    <p className="mt-1.5 text-[11.5px] text-indigo-700 leading-snug">
                                        This small payment verifies your UPI autopay setup. The amount will be automatically refunded within 24–48 hours of successful verification.
                                    </p>
                                </div>

                                
                                <div className="mt-4 p-4 bg-white border border-gray-100 shadow-sm rounded-2xl">
                                    <p className="text-[12.5px] font-semibold text-gray-900 mb-3">How it works</p>
                                    <div className="relative space-y-4">
                                        
                                        <div className="absolute left-[10px] top-1 bottom-1 w-px bg-gray-200" />

                                        <div className="relative flex gap-3">
                                            <div className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[10px] font-bold text-white">1</div>
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Get UPI Request</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Click the button below to generate a payment request to your UPI ID</p>
                                            </div>
                                        </div>

                                        <div className="relative flex gap-3">
                                            <div className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[10px] font-bold text-white">2</div>
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Open your UPI app</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Open the UPI app (GPay, PhonePe, Paytm) associated with this UPI ID</p>
                                            </div>
                                        </div>

                                        <div className="relative flex gap-3">
                                            <div className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[10px] font-bold text-white">3</div>
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Complete Payment</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Pay ₹2 to activate UPI autopay. The refund will be processed in 24–48 hours</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1" />

                                <button
                                    onClick={handleGetUpiRequest}
                                    className="w-full py-3 mb-4 text-m font-semibold text-white rounded-full bg-gradient-to-r from-indigo-500 to-blue-400"
                                >
                                    Get UPI Request
                                </button>

                                <PoweredByFooter />
                            </div>
                        )} */}

                    {/* ================= STEP: UPI Requesting (loader) ================= */}
                    {/* {step === "upiRequesting" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8 text-center bg-white">
                            <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
                            <p className="mt-4 text-[13.5px] font-semibold text-gray-800">
                                Generating payment request
                            </p>
                            <p className="mt-1 text-[11.5px] text-gray-500 leading-snug max-w-[260px]">
                                Sending a ₹2 collect request to {vpa}
                            </p>
                            <PoweredByFooter className="absolute bottom-6" />
                        </div>
                    )} */}
                    {/* ================= STEP: UPI Payment Waiting ================= */}
                    {step === "upiPaymentWaiting" && (
                        <div className="absolute inset-0 z-30 bg-white overflow-y-auto">
                            <div className="min-h-full flex flex-col items-center justify-center px-6 pt-8 pb-16 text-center">
                                <div className="flex flex-col items-center text-center">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 shadow-lg">
                                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                                    </div>
                                    <p className="mt-3 text-[13.5px] font-semibold text-gray-800">
                                        Complete your setup
                                    </p>
                                    <p className="mt-1 text-[11.5px] text-gray-500">
                                        This request will expire in{" "}
                                        <span className="font-semibold text-indigo-600">{formatTime(paymentSecondsLeft)}</span>
                                    </p>
                                </div>

                                <div className="mt-5 w-full max-w-[280px]">
                                    <div className="space-y-2.5">
                                        <div className="flex gap-2.5 p-3 bg-white border border-gray-100 shadow-sm rounded-xl text-left">
                                            <Smartphone className="h-4.5 w-4.5 shrink-0 text-indigo-500 mt-0.5" />
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Open your UPI app</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Go to your preferred UPI app linked to your bank account</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2.5 p-3 bg-white border border-gray-100 shadow-sm rounded-xl text-left">
                                            <ListChecks className="h-4.5 w-4.5 shrink-0 text-indigo-500 mt-0.5" />
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Select the pending request</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Look for the pending AutoPay request</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2.5 p-3 bg-white border border-gray-100 shadow-sm rounded-xl text-left">
                                            <KeyRound className="h-4.5 w-4.5 shrink-0 text-indigo-500 mt-0.5" />
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Review & Authorize</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">Verify the details, then enter your UPI PIN to approve the Autopay</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2.5 p-3 bg-white border border-gray-100 shadow-sm rounded-xl text-left">
                                            <RotateCcw className="h-4.5 w-4.5 shrink-0 text-indigo-500 mt-0.5" />
                                            <div>
                                                <p className="text-[12px] font-semibold text-gray-900">Return to this page</p>
                                                <p className="text-[11px] text-gray-500 leading-snug">We'll automatically verify the mandate once it's successfully approved</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 -mx-4 flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-left">
                                        <Info className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                                        <p className="text-[10.5px] leading-snug text-amber-800">
                                            As per NPCI guidelines, the maximum amount that can be auto-debited per transaction is ₹15,000.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <PoweredByFooter className="absolute bottom-6 left-0 right-0" />
                        </div>
                    )}

                    {/* ================= STEP: UPI Completed ================= */}
                    {step === "upiCompleted" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8 text-center bg-white">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 shadow-lg animate-[popIn_0.4s_ease-out]">
                                <CheckCircle2 className="h-10 w-10 text-white" />
                            </div>
                            <p className="mt-4 text-[14.5px] font-semibold bg-gradient-to-r from-emerald-500 to-emerald-400 bg-clip-text text-transparent">
                                UPI AutoPay Activated
                            </p>
                            <p className="mt-1 text-[12px] text-gray-500 leading-snug max-w-[260px]">
                                Your UPI ID {vpa} has been linked for auto-debit payments.
                            </p>
                            <PoweredByFooter className="absolute bottom-6" />
                            <style>
                                {`
                @keyframes popIn {
                    0% { transform: scale(0.4); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}
                            </style>
                        </div>
                    )}

                    {/* ================= STEP: Manual Form ================= */}
                    {(step === "manualForm" ||
                        step === "manualRedirected" ||
                        step === "manualCompleted") && (
                            <div className="flex flex-1 flex-col px-6 pt-3 pb-4 overflow-y-auto">
                                <div className="relative mt-3">
                                    <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_8px_16px_rgba(15,23,42,0.05)]">
                                        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />

                                        <div className="relative z-10 space-y-5">

                                            {bankingAlreadySaved ? (
                                                <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                                                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                                                    <div>
                                                        <p className="text-[13px] font-semibold text-emerald-800">
                                                            Banking details already saved
                                                        </p>
                                                        <p className="text-[11px] text-emerald-600">
                                                            Continue to complete your mandate authorization
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* IFSC Code */}
                                                    <div>
                                                        <label className="text-[11px] font-semibold text-gray-700">
                                                            IFSC Code
                                                        </label>

                                                        <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-transparent-50 px-3 py-2.5">
                                                            <Hash className="h-4 w-4 shrink-0 text-gray-400" />

                                                            <input
                                                                ref={firstInputRef}
                                                                type="text"
                                                                value={details.ifsc}
                                                                onChange={(e) =>
                                                                    handleChange(
                                                                        "ifsc",
                                                                        e.target.value
                                                                            .toUpperCase()
                                                                            .replace(/\s/g, "")
                                                                    )
                                                                }
                                                                placeholder="e.g. SBIN0000213"
                                                                maxLength={11}
                                                                className="w-full min-w-0 bg-transparent text-[13.5px] font-semibold tracking-wide text-gray-800 outline-none placeholder:text-gray-400"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Account Number */}
                                                    <div>
                                                        <label className="text-[11px] font-semibold text-gray-700">
                                                            Account Number
                                                        </label>

                                                        <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-transparent px-3 py-2.5">
                                                            <Wallet className="h-4 w-4 shrink-0 text-gray-400" />

                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                value={details.accountNumber}
                                                                onChange={(e) =>
                                                                    handleChange(
                                                                        "accountNumber",
                                                                        e.target.value.replace(/[^0-9]/g, "")
                                                                    )
                                                                }
                                                                placeholder="Enter account number"
                                                                className="w-full min-w-0 bg-transparent text-[13.5px] font-semibold text-gray-800 outline-none placeholder:text-gray-400"
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    </div>
                                </div>



                                <div className="flex-1" />
                                {error && (
                                    <div
                                        role="alert"
                                        aria-live="assertive"
                                        className="
            mb-3
            flex
            items-center
            gap-2
            rounded-lg
            border
            border-red-200
            bg-red-50
            px-3
            py-2
        "
                                    >
                                        <div
                                            className="
                flex
                h-6
                w-6
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-red-100
            "
                                        >
                                            <span
                                                className="
                    text-[11px]
                    font-bold
                    text-red-600
                "
                                            >
                                                !
                                            </span>
                                        </div>

                                        <p
                                            className="
                flex-1
                text-left
                text-[11px]
                font-medium
                leading-relaxed
                text-red-600
            "
                                        >
                                            {error}
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={handleContinueManual}
                                    disabled={!isFormComplete || isSubmitting}
                                    className="
        w-full
        py-3
        mb-4
        text-sm
        font-semibold
        text-white
        rounded-full
        bg-gradient-to-r
        from-indigo-500
        to-blue-400
        disabled:from-indigo-300
        disabled:to-blue-300
        disabled:opacity-70
        disabled:cursor-not-allowed
        flex
        items-center
        justify-center
        gap-2
    "
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Redirecting to E-mandate setup
                                        </>
                                    ) : bankingAlreadySaved ? (
                                        "Continue to Mandate"
                                    ) : (
                                        "Continue"
                                    )}
                                </button>

                                <PoweredByFooter />
                            </div>
                        )}


                    {/* ================= STEP: Manual Redirected / Waiting ================= */}
                    {step === "manualRedirected" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8 text-center bg-white">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full shadow-lg bg-gradient-to-r from-indigo-500 to-blue-400">
                                <Loader2 className="h-7 w-7 text-white animate-spin" />
                            </div>
                            <p className="mt-3 text-[13.5px] font-semibold text-gray-800">
                                Complete the mandate step
                            </p>
                            <p className="mt-1 text-[11.5px] text-gray-500 leading-snug max-w-[270px]">
                                We've opened the mandate authorization page in a new window. Submit the details there to continue.
                            </p>
                            <button
                                onClick={handleReopen}
                                className="mt-3 flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3.5 py-1.5 text-[11.5px] font-semibold text-indigo-600"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Reopen mandate window
                            </button>
                            <PoweredByFooter className="absolute bottom-6" />
                        </div>
                    )}


                    {/* ================= STEP: Manual Completed ================= */}
                    {step === "manualCompleted" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8 text-center bg-white">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full shadow-lg bg-gradient-to-r from-emerald-400 to-emerald-300">
                                <CheckCircle2 className="h-8 w-8 text-white" />
                            </div>
                            <p className="mt-3 text-[13.5px] font-semibold text-gray-800">
                                Mandate Setup Complete
                            </p>
                            <p className="mt-1 text-[11.5px] text-gray-500 leading-snug max-w-[270px]">
                                Your bank account has been linked for auto-debit payments.
                            </p>
                            <PoweredByFooter className="absolute bottom-6" />
                        </div>
                    )}

                </div>
            </div>
        </MobileFrame>
    );
};

export default SetupAutopay;